"use client";
"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Button = void 0;
const react_1 = __importDefault(require("react"));
const foundations_1 = require("../../foundations");
const Icon_1 = require("../../foundations/Icon/Icon");
const getResponsiveStyle_1 = require("../../utils/getResponsiveStyle");
const Button = (props) => {
    const { variant = "filled", brand = "primary", size = "medium", icon, iconPosition = "start", disabled = false, "aria-label": ariaLabel, children, sx } = props, restProps = __rest(props, ["variant", "brand", "size", "icon", "iconPosition", "disabled", "aria-label", "children", "sx"]);
    const isIconButton = icon && !children;
    if (isIconButton && !ariaLabel) {
        throw new Error("Accessibility error: Icon-only buttons must have an aria-label.");
    }
    const iconButtonBaseStyles = {
        width: (0, getResponsiveStyle_1.getResponsiveStyle)(size, (s) => s === "medium" ? "spacing-9" : s === "large" ? "spacing-10" : "spacing-12"),
        p: (0, getResponsiveStyle_1.getResponsiveStyle)(size, (s) => s === "medium" ? "spacing-3" : "spacing-4")
    };
    const iconSize = (0, getResponsiveStyle_1.getResponsiveStyle)(size, (s) => isIconButton
        ? s === "medium"
            ? "xs"
            : s === "large"
                ? "sm"
                : "md"
        : s === "medium"
            ? "xxs"
            : s === "large"
                ? "xs"
                : "sm");
    const buttonBaseStyles = {
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        flexShrink: 0,
        height: (0, getResponsiveStyle_1.getResponsiveStyle)(size, (s) => s === "medium" ? "spacing-9" : s === "large" ? "spacing-10" : "spacing-12"),
        borderRadius: "radius-2",
        px: "spacing-5",
        py: "spacing-4",
        gap: "spacing-2",
        fontSize: (0, getResponsiveStyle_1.getResponsiveStyle)(size, (s) => s === "medium"
            ? "font-size-3"
            : s === "large"
                ? "font-size-4"
                : "font-size-5"),
        fontWeight: "font-weight-medium",
        lineHeight: "leading-none",
        letterSpacing: "kerning-none",
        transition: "transition-color-medium"
    };
    const buttonColorsFilled = react_1.default.useMemo(() => ({
        backgroundColor: disabled
            ? "button-surface-filled-disabled"
            : brand === "primary"
                ? "button-surface-filled-enabled"
                : "button-surface-filled-secondary-enabled",
        color: disabled
            ? "button-onSurface-filled-disabled"
            : brand === "primary"
                ? "button-onSurface-filled-primary"
                : "button-onSurface-filled-secondary",
        ":hover": disabled
            ? {}
            : {
                backgroundColor: brand === "primary"
                    ? "button-surface-filled-hover"
                    : "button-surface-filled-secondary-hover"
            },
        ":active": disabled
            ? {}
            : {
                backgroundColor: brand === "primary"
                    ? "button-surface-filled-click"
                    : "button-surface-filled-secondary-click"
            }
    }), [brand, disabled]);
    const buttonColorsOutline = react_1.default.useMemo(() => ({
        backgroundColor: disabled
            ? "button-surface-outline-disabled"
            : brand === "primary"
                ? "button-surface-outline-primary"
                : "button-surface-outline-secondary",
        color: disabled
            ? "button-onSurface-outline-disabled"
            : brand === "primary"
                ? "button-onSurface-outline-enabled"
                : "button-onSurface-outline-secondary-enabled",
        border: "1px solid",
        borderColor: disabled
            ? "button-onSurface-outline-disabled"
            : brand === "primary"
                ? "button-onSurface-outline-enabled"
                : "button-onSurface-outline-secondary-enabled",
        ":hover": disabled
            ? {}
            : {
                color: brand === "primary"
                    ? "button-onSurface-outline-hover"
                    : "button-onSurface-outline-secondary-hover",
                borderColor: brand === "primary"
                    ? "button-onSurface-outline-hover"
                    : "button-onSurface-outline-secondary-hover"
            },
        ":active": disabled
            ? {}
            : {
                color: brand === "primary"
                    ? "button-onSurface-outline-click"
                    : "button-onSurface-outline-secondary-click",
                borderColor: brand === "primary"
                    ? "button-onSurface-outline-click"
                    : "button-onSurface-outline-secondary-click"
            }
    }), [brand, disabled]);
    const buttonColorsSubtle = react_1.default.useMemo(() => ({
        color: disabled
            ? "button-onSurface-subtle-disabled"
            : brand === "primary"
                ? "button-onSurface-subtle-enabled"
                : "button-onSurface-subtle-secondary-enabled",
        ":hover": disabled
            ? {}
            : {
                color: brand === "primary"
                    ? "button-onSurface-subtle-hover"
                    : "button-onSurface-subtle-secondary-hover"
            },
        ":active": disabled
            ? {}
            : {
                color: brand === "primary"
                    ? "button-onSurface-subtle-click"
                    : "button-onSurface-subtle-secondary-click"
            }
    }), [brand, disabled]);
    const buttonColorCritical = react_1.default.useMemo(() => ({
        backgroundColor: disabled
            ? "button-surface-critical-disabled"
            : "button-surface-critical-enabled",
        color: disabled
            ? "button-onSurface-critical-disabled"
            : "button-onSurface-critical",
        ":hover": disabled
            ? {}
            : {
                backgroundColor: "button-surface-critical-hover"
            },
        ":active": disabled
            ? {}
            : {
                backgroundColor: "button-surface-critical-click"
            }
    }), [disabled]);
    const variantStyles = react_1.default.useMemo(() => {
        switch (variant) {
            case "filled":
                return buttonColorsFilled;
            case "outline":
                return buttonColorsOutline;
            case "subtle":
                return buttonColorsSubtle;
            case "critical":
                return buttonColorCritical;
        }
    }, [
        variant,
        buttonColorsFilled,
        buttonColorsOutline,
        buttonColorsSubtle,
        buttonColorCritical
    ]);
    const buttonFocusStyles = react_1.default.useMemo(() => ({
        ":focus-visible": {
            outline: "2px solid",
            outlineColor: "focus",
            outlineOffset: "2px"
        }
    }), []);
    const renderIcon = () => {
        return (react_1.default.createElement(Icon_1.Icon, { icon: icon, size: iconSize, color: "inherit", sx: { flexShrink: 0 } }));
    };
    return (react_1.default.createElement(foundations_1.Target, Object.assign({ sx: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, buttonBaseStyles), (isIconButton && iconButtonBaseStyles)), variantStyles), buttonFocusStyles), sx), disabled: disabled, "aria-label": ariaLabel }, restProps),
        icon && iconPosition === "start" && renderIcon(),
        children,
        icon && iconPosition === "end" && renderIcon()));
};
exports.Button = Button;
//# sourceMappingURL=Button.js.map